import React from 'react'
import { type StyledComponent, styled, css } from 'styled-components'
import { getAspectSize } from '../views/Screens/hook'
import {
	CONTAINER_ASPECT_RATIO,
	MIN_PRE_SCALE_DIMENSIONS,
	CSS_PLAYER_WIDTH_UNIT,
	CSS_PLAYER_HEIGHT_UNIT,
} from '../../utility/constants'

import {
	aspectRatioHeightCss,
	aspectRatioWidthCss,
} from '../../utility/functions'

/**
 * A wrapper that scales its children based on the width/height of its parent or the canvas scale dimensions.
 * Also adds global variable units such as --ph and --pw which are used throughout the project.
 * @param {?number} props.width
 * @param {?number} props.height
 * @param {React$Node} props.children
 * @param {?{}} props.style
 * @param {?string} props.className
 * @returns {React$Node}
 */
export default function SizingWrapper({
	width,
	height,
	style,
	...props
}: {|
	width: ?number,
	height: ?number,
	style?: {},
	id?: string,
	className?: string,
	children?: React$Node,
|}): React$Node {
	let scale = 1
	if (width && height) {
		let canvasWidth = Math.max(width || 0, MIN_PRE_SCALE_DIMENSIONS.width)
		let canvasHeight = Math.max(height || 0, MIN_PRE_SCALE_DIMENSIONS.height)

		const { width: canvasAspectWidth, height: canvasAspectHeight } =
			getAspectSize(canvasWidth, canvasHeight)

		scale = Math.min(
			width / (canvasAspectWidth || 1),
			height / (canvasAspectHeight || 1)
		)
	}

	return (
		<Wrapper
			width={width || 0}
			height={height || 0}
			style={{ ...style, transform: `scale(${scale})` }}
			{...props}
		/>
	)
}

const Wrapper: StyledComponent<
	{ width: number, height: number, style?: {}, className?: string },
	{||},
	HTMLDivElement
> = styled.div`
	position: relative;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	${({ width, height }) =>
		`height: ${aspectRatioHeightCss(
			`max(${width}px, ${MIN_PRE_SCALE_DIMENSIONS.width}px)`,
			`max(${height}px, ${MIN_PRE_SCALE_DIMENSIONS.height}px)`,
			CONTAINER_ASPECT_RATIO
		)};
	width: ${aspectRatioWidthCss(
		`max(${width}px, ${MIN_PRE_SCALE_DIMENSIONS.width}px)`,
		`max(${height}px, ${MIN_PRE_SCALE_DIMENSIONS.height}px)`,
		CONTAINER_ASPECT_RATIO
	)};
	font-size: calc(
		${aspectRatioHeightCss(
			`max(${width}px, ${MIN_PRE_SCALE_DIMENSIONS.width}px)`,
			`max(${height}px, ${MIN_PRE_SCALE_DIMENSIONS.height}px)`,
			CONTAINER_ASPECT_RATIO
		)} / 100
	);
	${CSS_PLAYER_HEIGHT_UNIT}: calc(${aspectRatioHeightCss(
			`max(${width}px, ${MIN_PRE_SCALE_DIMENSIONS.width}px)`,
			`max(${height}px, ${MIN_PRE_SCALE_DIMENSIONS.height}px)`,
			CONTAINER_ASPECT_RATIO
		)} / 100
	);
	${CSS_PLAYER_WIDTH_UNIT}: calc(${aspectRatioWidthCss(
			`max(${width}px, ${MIN_PRE_SCALE_DIMENSIONS.width}px)`,
			`max(${height}px, ${MIN_PRE_SCALE_DIMENSIONS.height}px)`,
			CONTAINER_ASPECT_RATIO
		)} / 100
	);
		`}

	${({ width, height }) =>
		// Code to help the controller scale well
		width &&
		height &&
		css`
			position: absolute;
			transform-origin: left top;
		`}
`
